/**
 * What's new (/changelog, Paper "What's new (changelog)", boards W1 and W1b):
 * Crew's updates, newest first, by month, with a filter by category. Opened
 * from the row under Memory (the sidebar, the phone's More); opening it marks
 * every update seen, so the row's dot (and More's) goes.
 */
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { PageHeader } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { useEffect, useState } from "react";
import { DocMarkdown } from "../docs/doc-markdown";
import {
  byMonth,
  CATEGORY_LABELS,
  dayLabel,
  markSeen,
  UPDATES,
  type Update,
  type UpdateCategory,
} from "./changelog";

type Filter = "all" | UpdateCategory;

const FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "feature", label: "Features" },
  { value: "improvement", label: "Improvements" },
  { value: "fix", label: "Fixes" },
];

const TAG_TONES: Record<UpdateCategory, string> = {
  feature: "bg-copper/10 text-copper",
  improvement: "bg-moss/10 text-moss",
  fix: "bg-foreground/8 text-foreground",
};

export function ChangelogPage({
  userId,
  updates = UPDATES,
}: {
  userId: string;
  updates?: Update[];
}) {
  const [filter, setFilter] = useState<Filter>("all");
  useEffect(() => markSeen(userId, updates), [userId, updates]);
  const shown = filter === "all" ? updates : updates.filter((u) => u.category === filter);
  return (
    <main
      data-changelog-page
      className="flex w-full max-w-[816px] flex-col gap-6 px-4 pt-5 pb-16 sm:px-6 md:px-12 md:pt-9"
    >
      <PageHeader title="What's new" description="What's changed in Crew lately, newest first." />
      <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
        {/* On a phone the tabs scroll sideways rather than wrap. */}
        <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0">
          <TabsList aria-label="Filter updates">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value} data-filter={f.value}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing here yet.</p>
      ) : (
        byMonth(shown).map((group) => (
          <section
            key={group.month}
            aria-label={group.month}
            className="flex flex-col pt-1 md:pt-3"
          >
            <h2 className="border-b border-foreground/14 pb-2 font-display text-[26px] leading-8 text-foreground md:text-title">
              {group.month}
            </h2>
            <ol>
              {group.updates.map((update) => (
                <UpdateRow key={update.id} update={update} />
              ))}
            </ol>
          </section>
        ))
      )}
    </main>
  );
}

function UpdateRow({ update }: { update: Update }) {
  const date = dayLabel(update.date);
  return (
    <li
      data-update={update.id}
      className="flex gap-6 border-b border-foreground/8 py-[18px] last:border-b-0 md:py-[22px]"
    >
      {/* The date's own lane on a wide screen; beside the tag on a phone. */}
      <time
        dateTime={update.date}
        className="hidden w-[72px] shrink-0 pt-[3px] font-mono text-meta leading-4 text-hint md:block"
      >
        {date}
      </time>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              "rounded-[4px] px-1.5 py-0.5 text-xs leading-4 font-semibold",
              TAG_TONES[update.category],
            )}
          >
            {CATEGORY_LABELS[update.category]}
          </span>
          <span aria-hidden className="text-xs leading-4 text-hint md:hidden">
            {date}
          </span>
        </div>
        <h3 className="text-base leading-[22px] font-semibold text-foreground md:text-lead md:leading-6">
          {update.title}
        </h3>
        <DocMarkdown className="space-y-2">{update.body}</DocMarkdown>
      </div>
    </li>
  );
}
